/**
 * Blueprint fragments (M8 plan MC-h5a, absorbs SY-8; T12 amended, call MS-6): a generic data core is read for a
 * blueprint fragment at a market station of `blueprints.json faction` (the Mutual), and every `fragmentsPerRung`
 * fragments add the next pattern-unlock id of `ladder` to `career.patterns.unlocked`, the one unlock store MC-f3's
 * standing and study routes write too.
 *
 * - **What:** a generic core only (`story/hooks.ts isGenericCore`): story items stay sealed (M7 S4, T13), and sealed
 *   or contract cargo is never handed over. Same gate and same retaliation refusal (`marketRefusal`
 *   `sellComponent`) as SY-7's `career/cores.ts deliverCore`.
 * - **The core leaves the hold**, so it can't be sold or delivered afterwards. `career.dataCores` and the story
 *   ledger keep its entry (copied on pickup, M7 S14), so it never logs again: never a lead or evidence, and the M7 S7
 *   clue budget is untouched. No credits move: the Hub stays the only core buyer (D2-FC #8).
 * - **Counter:** `career.blueprints.fragments` +1, up to `cap`. A core at the cap is refused (never thrown away).
 * - **Rungs:** the fragments earn `ladder[0..floor(fragments / fragmentsPerRung))` in ladder order
 *   (`holdfast_old.mk2` at 3, `holdfast_old.mk3` at 6). An id already unlocked (MC-f3's study) is not added twice.
 * - **Wording:** every player text avoids "decode": M7 Mission 1 decodes the flight recorder at the same Mutual
 *   stations (`comms/common.json contacts.m1Decode`). Texts live in `blueprints.json text`.
 *
 * Pure: Career in, Career out. `features/dataCores/feature.ts` registers `dataCore.decode` on the craft hook.
 */
import { z } from 'zod'
import type { CmdResult } from '../career/result'
import { fail, ok } from '../career/result'
import { withoutComponent } from '../career/ops'
import { PATTERN_UNLOCK_IDS, type Career, type PatternUnlockId } from '../career/types'
import { SIM_FACTION_IDS, type StationDef, type StationId } from '../data/m2Schemas'
import blueprintsJson from '../data/blueprints.json'
import { isGenericCore } from '../story/hooks'
import { marketRefusal } from '../story/refusals'

declare module './hook' {
  interface CraftCommandMap {
    /** Reads the hold data core `coreId` for a blueprint fragment at `stationId` (the docked station). */
    'dataCore.decode': { kind: 'dataCore.decode'; stationId: StationId; coreId: string }
  }
}

const textSchema = z.strictObject({
  /** `{station}`: the station's name. */
  noService: z.string().min(1),
  notInCargo: z.string().min(1),
  notCore: z.string().min(1),
  sealed: z.string().min(1),
  /** `{cap}`: the fragment cap. */
  full: z.string().min(1),
  /** The craft command ran away from the station it names. */
  notDocked: z.string().min(1),
  /** The Market core-row button (MC-h5b). `{have}`: fragments held; `{cap}`: the cap. */
  readButton: z.string().min(1),
})

export const blueprintsFileSchema = z
  .strictObject({
    /** The faction whose market stations read cores for fragments (the Mutual). */
    faction: z.enum(SIM_FACTION_IDS),
    /** Fragments per ladder rung. */
    fragmentsPerRung: z.number().int().positive(),
    /** The fragment counter's cap. */
    cap: z.number().int().positive(),
    /** Pattern-unlock ids in unlock order (unique). */
    ladder: z.array(z.enum(PATTERN_UNLOCK_IDS)),
    /** Player-facing refusals (no "decode": M1's word, `m1Decode`). */
    text: textSchema,
  })
  .refine((f) => new Set(f.ladder).size === f.ladder.length, { message: 'ladder ids must be unique', path: ['ladder'] })
  .refine((f) => f.cap >= f.fragmentsPerRung * f.ladder.length, { message: 'cap must reach every ladder rung', path: ['cap'] })
  .refine((f) => Object.values(f.text).every((t) => !/decod/i.test(t)), { message: "texts must avoid M1's \"decode\" wording", path: ['text'] })
export type BlueprintsFile = z.infer<typeof blueprintsFileSchema>

/** Parses `blueprints.json`; throws naming the bad path. */
export function loadBlueprints(raw: unknown = blueprintsJson): BlueprintsFile {
  return blueprintsFileSchema.parse(raw)
}

/** The shipped file. */
export const BLUEPRINTS: BlueprintsFile = loadBlueprints()

/** The career's fragment count (absent = 0). */
export function fragmentsOf(career: Career): number {
  return career.blueprints?.fragments ?? 0
}

/** The ladder ids `fragments` have earned, in ladder order. */
export function rungsEarned(fragments: number, file: BlueprintsFile = BLUEPRINTS): PatternUnlockId[] {
  const rungs = Math.min(file.ladder.length, Math.floor(fragments / file.fragmentsPerRung))
  return file.ladder.slice(0, Math.max(0, rungs))
}

/**
 * Adds `ids` to `career.patterns.unlocked` in the given order, skipping ids already there. Returns `career` itself
 * when nothing is new. The one write path this module has into the shared unlock store.
 */
export function withPatternUnlocks(career: Career, ids: readonly PatternUnlockId[]): Career {
  const have = career.patterns?.unlocked ?? []
  const add = ids.filter((id, i) => !have.includes(id) && ids.indexOf(id) === i)
  if (add.length === 0) return career
  return { ...career, patterns: { ...career.patterns, unlocked: [...have, ...add] } }
}

/** True when `station` reads cores for fragments: a market station of the file's faction. */
export function decodesCoresAt(station: Pick<StationDef, 'faction' | 'services'>, file: BlueprintsFile = BLUEPRINTS): boolean {
  return station.faction === file.faction && station.services.includes('market')
}

/**
 * Reads the generic hold core `coreId` for a blueprint fragment at `station`: the core leaves the hold, the counter
 * goes +1 and any rung it reaches adds its pattern-unlock id. Refuses (Career untouched) at a station that doesn't
 * read cores, under a retaliation refusal, for a component not in the hold, not a core, a story, sealed or contract
 * core, or with the counter at its cap.
 */
export function decodeCore(
  career: Career,
  station: Pick<StationDef, 'id' | 'name' | 'faction' | 'services'>,
  coreId: string,
  file: BlueprintsFile = BLUEPRINTS,
): CmdResult<Career> {
  if (!decodesCoresAt(station, file)) {
    return fail('serviceUnavailable', file.text.noService.replace('{station}', station.name), { station: station.id })
  }
  const refused = marketRefusal(career, station, 'sellComponent')
  if (refused !== null) return refused
  const core = career.cargo.components.find((c) => c.id === coreId)
  if (core === undefined) return fail('notInCargo', file.text.notInCargo, { id: coreId })
  if (core.type !== 'dataCore') return fail('notBoughtHere', file.text.notCore, { id: core.id })
  if (!isGenericCore(core)) return fail('notBoughtHere', file.text.sealed, { id: core.id })
  const before = fragmentsOf(career)
  if (before >= file.cap) return fail('invalidAmount', file.text.full.replace('{cap}', String(file.cap)), { cap: file.cap })
  const fragments = before + 1
  const taken = { ...withoutComponent(career, core.id), blueprints: { fragments } }
  return ok(withPatternUnlocks(taken, rungsEarned(fragments, file)))
}
